import { useCallback, useMemo, useRef, useState } from 'react';

import {
  getRecommendChatErrorCode,
  getRecommendChatErrorMessage,
  useResolveRecommendChatBindingMutation,
} from '@app/api/RecommendChat';
import type { RecommendBindingConflict } from '@app/api/RecommendChat';
import { useUserPrint } from '@app/hooks';

interface UseLoyaltyBindingConflictParams {
  conflict: RecommendBindingConflict;
  onResolved(): void;
}

export const useLoyaltyBindingConflict = ({
  conflict,
  onResolved,
}: UseLoyaltyBindingConflictParams) => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const [selection, setSelection] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resultText, setResultText] = useState<string | null>(null);
  const [resolveBinding, { isLoading }] =
    useResolveRecommendChatBindingMutation();
  const submittingRef = useRef(false);
  const options = useMemo(
    () =>
      conflict.candidates.map(({ clientId, leadId, name, branchName }) => ({
        value: `${clientId}:${leadId}`,
        label: `${name} — ${branchName}${leadId === null ? ', без лида' : ''}`,
      })),
    [conflict.candidates]
  );
  const selectionChangeHandler = useCallback(
    (value: string) => setSelection(value),
    []
  );
  const reasonChangeHandler = useCallback(
    (value: string) => setReason(value),
    []
  );
  const resolveHandler = useCallback(async () => {
    if (
      !ActiveBranchId ||
      submittingRef.current ||
      !selection ||
      !reason.trim() ||
      reason.length > 1000
    )
      return;
    const candidate = conflict.candidates.find(
      ({ clientId, leadId }) => `${clientId}:${leadId}` === selection
    );
    if (!candidate) return;
    submittingRef.current = true;
    setError(null);
    try {
      const result = await resolveBinding({
        ActiveBranchId,
        data: {
          conversationId: conflict.conversationId,
          bindingRevision: conflict.bindingRevision,
          operation: 'linkExisting',
          clientId: candidate.clientId,
          leadId: candidate.leadId,
          reason: reason.trim(),
        },
      }).unwrap();
      setResultText(
        result.status === 'READY'
          ? 'Обращение привязано.'
          : result.status === 'PENDING'
          ? 'Решение принято. Обращение обрабатывается.'
          : 'Привязка требует повторной проверки.'
      );
      onResolved();
    } catch (error) {
      if (getRecommendChatErrorCode(error) === 'VERSION_CONFLICT') {
        setSelection(null);
        setError('Привязка уже изменена. Список обращений обновляется.');
        onResolved();
      } else {
        setError(getRecommendChatErrorMessage(error));
      }
    } finally {
      submittingRef.current = false;
    }
  }, [ActiveBranchId, conflict, onResolved, reason, resolveBinding, selection]);

  return {
    options,
    selection,
    reason,
    error,
    resultText,
    isLoading,
    canResolve: Boolean(
      options.some(({ value }) => value === selection) &&
        reason.trim() &&
        reason.length <= 1000 &&
        !isLoading
    ),
    selectionChangeHandler,
    reasonChangeHandler,
    resolveHandler,
  };
};
